import { DateTimePicker, FormControl, FormLabel } from '@wegroup/design-system';
import React, { useState } from 'react';
import styled from 'styled-components';
import { mq } from '../../../../../common/src/utils/styledComponentsUtils';

// Components
import SearchSelectInputV2 from '../../../../../common/src/components/SearchSelectInputV2';

// Types
import { useDispatch, useSelector } from 'react-redux';
import {
  setContractStartDate,
  addToQuoteSpecifications,
} from '../../../store/redux/slices/offersSlice';
import { getProductKey, OfferState } from '../../../services/offersService';
import { LouiseOffer } from '../../../../../../types/Quotes';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store/redux/reducers/rootReducer';

interface Props {
  offer: LouiseOffer;
}

export const defaultPaymentIntervals = [
  'annual',
  'monthly',
  'quarterly',
  'half_annual',
];

const SingleSelectedOfferForm: React.FC<React.PropsWithChildren<Props>> = ({
  offer,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const contractStartDateState = useSelector(
    (state: RootState) => state.offers.offersInfo.contractStartDate,
  );
  const contractStartDate =
    contractStartDateState[offer.hash_id] || new Date().toISOString();

  const offerInterval =
    useSelector(
      (state: RootState) =>
        state.offers.quoteSpecs[getProductKey(offer)]?.payment_interval,
    ) ||
    offer.details?.payment_interval ||
    'ANNUAL';

  // If none are given, it defaults the the current offer interval being set
  const [intervalOptions] = useState(
    offer.details
      ? offer.details.payment_intervals.map((value) => ({
          label: t(`spread.${value.toUpperCase()}`),
          value,
        }))
      : defaultPaymentIntervals.map((value) => ({
          label: t(`spread.${value.toUpperCase()}`),
          value,
        })),
  );

  return (
    <FormBox>
      <FormControl>
        <FormLabel>{t('Payment interval')}</FormLabel>
        <SearchSelectInputV2
          name="payment-interval"
          onSelected={(option) =>
            dispatch(
              addToQuoteSpecifications({
                productKey: getProductKey(offer),
                quoteId: offer.hash_id,
                value: { payment_interval: option.value as string },
              }),
            )
          }
          options={intervalOptions}
          value={intervalOptions.find(
            ({ value }) => value.toLowerCase() === offerInterval.toLowerCase(),
          )}
          disabled={offer.state === OfferState.LOADING}
        />
      </FormControl>
      <FormControl>
        <FormLabel>{t('Contract start date')}</FormLabel>
        <DateTimePicker
          date={new Date(contractStartDate)}
          onChange={(date) =>
            dispatch(
              setContractStartDate({
                quoteId: offer.hash_id,
                text: date.toISOString(),
              }),
            )
          }
          showTimeSelect={false}
        />
      </FormControl>
    </FormBox>
  );
};

const FormBox = styled.div`
  display: grid;
  grid-gap: 1.6rem;
  margin-bottom: auto;

  ${mq.laptop} {
    margin-top: 1.6rem;
  }
`;

export default SingleSelectedOfferForm;
